import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getClasses, getSubjects, postClass, postSubject, getClassLevels, postClassLevel, deleteClassLevel,
  putClass, putClassLevel, putSubject, assignTeacherToClass, getStaff,
  getAssessmentComponents, putAssessmentComponent, deleteClass, deleteSubject,
  getTeacherAssignments, postTeacherAssignment, deleteTeacherAssignment,
} from '@/api/owner';
import { Plus, X, Edit2, UserCheck, Trash2, AlertTriangle, Search, Users, BookOpen, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/stores/auth';

/* ───────────────────────── helpers ───────────────────────── */

const list = (res: any): any[] => (Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);

/** "Ada Okafor" from first/last name, falling back to the account name. */
function personName(p: any): string {
  if (!p) return '';
  if (typeof p === 'string') return p;
  const full = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return full || p.name || '';
}

function teacherOf(a: any): string {
  return a.teacher_name
    || [a.teacher_first_name, a.teacher_last_name].filter(Boolean).join(' ').trim()
    || (typeof a.teacher === 'string' ? a.teacher : personName(a.teacher))
    || `Staff #${a.teacher_id}`;
}
const classLabel = (c: any) => [c?.name, c?.arm].filter(Boolean).join(' ');
const classOf = (a: any, byId: Map<number, any>) =>
  a.class_name || [a.class, a.arm].filter(Boolean).join(' ') || classLabel(byId.get(a.school_class_id)) || '—';
const subjectOf = (a: any, byId: Map<number, any>) =>
  a.subject_name || (typeof a.subject === 'string' ? a.subject : a.subject?.name) || byId.get(a.subject_id)?.name || 'Class teacher';

function errorText(e: any): string | null {
  if (!e) return null;
  const d = e.response?.data;
  const first = d?.errors && Object.values(d.errors).flat()[0];
  return (first as string) || d?.message || e.message || 'Something went wrong.';
}

const initials = (n: string) => n.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

/* Hover-only on desktop, always visible on touch screens. */
const rowActions = 'flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity';

/* ───────────────────────── page ───────────────────────── */

export function AcademicsPage() {
  const user = useAuth((s) => s.user);
  const schoolName = user?.schoolName ?? 'Your School';
  const queryClient = useQueryClient();

  const [modalType, setModalType] = useState<'class' | 'subject' | 'level' | 'teacher' | 'weight' | 'assignment' | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'level' | 'class' | 'subject' | 'assignment'; item: any } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Assignment filters
  const [fClass, setFClass] = useState('');
  const [fTeacher, setFTeacher] = useState('');
  const [fSearch, setFSearch] = useState('');

  // Queries
  const { data: clsRes } = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses().then((r: any) => r.data?.data ?? r.data) });
  const { data: subRes } = useQuery({ queryKey: ['owner-subjects'], queryFn: () => getSubjects().then((r: any) => r.data?.data ?? r.data) });
  const { data: lvlRes } = useQuery({ queryKey: ['owner-levels'], queryFn: () => getClassLevels().then((r: any) => r.data?.data ?? r.data) });
  const { data: staffRes } = useQuery({ queryKey: ['owner-staff', 'active-all'], queryFn: () => getStaff({ status: 'active', per_page: 100 }).then((r: any) => r.data?.data ?? r.data) });
  const { data: compRes } = useQuery({ queryKey: ['owner-components'], queryFn: () => getAssessmentComponents().then((r: any) => r.data?.data ?? r.data) });
  const { data: assignRes, isLoading: assignLoading } = useQuery({ queryKey: ['owner-assignments'], queryFn: () => getTeacherAssignments().then((r: any) => r.data?.data ?? r.data) });

  const classes = list(clsRes);
  const subjects = list(subRes);
  const levels = list(lvlRes);
  const staff = list(staffRes);
  const components = list(compRes);
  const assignments = list(assignRes);

  // Staff who can be assigned (backend accepts teacher, desk and owner accounts).
  const teachers = staff.filter((s) => s.role === 'teacher');
  const otherStaff = staff.filter((s) => s.role === 'desk' || s.role === 'owner');

  const classById = useMemo(() => new Map(classes.map((c) => [Number(c.id), c])), [classes]);
  const subjectById = useMemo(() => new Map(subjects.map((s) => [Number(s.id), s])), [subjects]);

  // Subject assignments only (class-teacher rows have no subject).
  const subjectAssignments = assignments.filter((a) => a.subject_id);

  /** subject_id -> list of "Teacher (Class)" */
  const teachersBySubject = useMemo(() => {
    const m = new Map<number, { teacher: string; cls: string }[]>();
    subjectAssignments.forEach((a) => {
      const k = Number(a.subject_id);
      m.set(k, [...(m.get(k) ?? []), { teacher: teacherOf(a), cls: classOf(a, classById) }]);
    });
    return m;
  }, [subjectAssignments, classById]);

  /** school_class_id -> number of subjects with a teacher */
  const subjectCountByClass = useMemo(() => {
    const m = new Map<number, Set<number>>();
    subjectAssignments.forEach((a) => {
      const k = Number(a.school_class_id);
      m.set(k, (m.get(k) ?? new Set()).add(Number(a.subject_id)));
    });
    return m;
  }, [subjectAssignments]);

  const filteredAssignments = useMemo(() => {
    const q = fSearch.trim().toLowerCase();
    return subjectAssignments.filter((a) => {
      if (fClass && String(a.school_class_id) !== fClass) return false;
      if (fTeacher && String(a.teacher_id) !== fTeacher) return false;
      if (q && !`${teacherOf(a)} ${classOf(a, classById)} ${subjectOf(a, subjectById)}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [subjectAssignments, fClass, fTeacher, fSearch, classById, subjectById]);

  const assignedTeacherIds = new Set(subjectAssignments.map((a) => String(a.teacher_id)));
  const assignedTeacherOptions = staff.filter((s) => assignedTeacherIds.has(String(s.id)));

  const invalidateAll = () => {
    ['owner-levels', 'owner-classes', 'owner-subjects', 'owner-components', 'owner-assignments'].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k] }));
    setModalType(null);
    setSelectedItem(null);
    setDeleteTarget(null);
  };
  const done = (msg?: string) => (res?: any) => { invalidateAll(); setNotice(res?.data?.message || msg || null); };

  const classMutation = useMutation({ mutationFn: (b: any) => (selectedItem ? putClass(selectedItem.id, b) : postClass(b)), onSuccess: done('Class saved.') });
  const levelMutation = useMutation({ mutationFn: (b: any) => (selectedItem ? putClassLevel(selectedItem.id, b) : postClassLevel(b)), onSuccess: done('Level saved.') });
  const subjectMutation = useMutation({ mutationFn: (b: any) => (selectedItem ? putSubject(selectedItem.id, b) : postSubject(b)), onSuccess: done('Subject saved.') });
  const teacherMutation = useMutation({
    mutationFn: (b: any) => assignTeacherToClass(selectedItem.id, { teacher_id: Number(b.teacher_id), is_form_teacher: true }),
    onSuccess: done('Class teacher assigned.'),
  });
  const weightMutation = useMutation({ mutationFn: (b: any) => putAssessmentComponent(selectedItem.id, b), onSuccess: done('Weight saved.') });
  const assignmentMutation = useMutation({ mutationFn: (b: any) => postTeacherAssignment(b), onSuccess: done('Teacher assigned.') });

  const deleteLevelMutation = useMutation({ mutationFn: (id: number) => deleteClassLevel(id), onSuccess: done('Level deleted.') });
  const deleteClassMutation = useMutation({ mutationFn: (id: number) => deleteClass(id), onSuccess: done('Class deleted.') });
  const deleteSubjectMutation = useMutation({ mutationFn: (id: number) => deleteSubject(id), onSuccess: done('Subject removed.') });
  const deleteAssignmentMutation = useMutation({ mutationFn: (id: number) => deleteTeacherAssignment(id), onSuccess: done('Assignment removed.') });
  const deleting = deleteLevelMutation.isPending || deleteClassMutation.isPending || deleteSubjectMutation.isPending || deleteAssignmentMutation.isPending;
  const deleteError = errorText(deleteLevelMutation.error || deleteClassMutation.error || deleteSubjectMutation.error || deleteAssignmentMutation.error);

  const openModal = (type: any, item: any = null) => {
    [classMutation, levelMutation, subjectMutation, teacherMutation, weightMutation, assignmentMutation].forEach((m) => m.reset());
    setSelectedItem(item);
    setModalType(type);
  };
  const closeModal = () => { setModalType(null); setSelectedItem(null); };

  const handleDelete = () => {
    if (!deleteTarget) return;
    const id = deleteTarget.item.id;
    if (deleteTarget.type === 'level') deleteLevelMutation.mutate(id);
    if (deleteTarget.type === 'class') deleteClassMutation.mutate(id);
    if (deleteTarget.type === 'subject') deleteSubjectMutation.mutate(id);
    if (deleteTarget.type === 'assignment') deleteAssignmentMutation.mutate(id);
  };

  const weightTotal = components.reduce((a, b) => a + Number(b.weight || 0), 0);
  const deleteLabel = deleteTarget?.type === 'assignment'
    ? `${teacherOf(deleteTarget.item)} · ${subjectOf(deleteTarget.item, subjectById)} (${classOf(deleteTarget.item, classById)})`
    : deleteTarget?.item?.name;

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto p-4 sm:p-6 pb-10">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 truncate">{schoolName}</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Academics</h1>
          <p className="text-slate-500 text-sm mt-1">Classes, subjects, grading and teacher assignments</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <button onClick={() => openModal('class')} className="bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg font-semibold text-sm shadow-sm hover:bg-slate-50 flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add class</button>
          <button onClick={() => openModal('subject')} className="bg-[#0f172a] hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-sm flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add subject</button>
        </div>
      </div>

      {notice && (
        <div className="flex items-center justify-between gap-3 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-4 py-2.5">
          <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 shrink-0" />{notice}</span>
          <button onClick={() => setNotice(null)} className="text-green-700 hover:text-green-900" aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* SUMMARY */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Classes" value={classes.length} />
        <Stat label="Subjects" value={subjects.length} />
        <Stat label="Subject assignments" value={subjectAssignments.length} />
        <Stat label="Classes without class teacher" value={classes.filter((c) => !c.form_teacher_id).length} tone={classes.some((c) => !c.form_teacher_id) ? 'amber' : 'green'} />
      </div>

      {/* CLASS LEVELS */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4">
          <div>
            <h3 className="font-bold text-slate-900 text-lg">Class Levels</h3>
            <p className="text-sm text-slate-500">Levels that classes can be assigned to (e.g., JSS, SSS).</p>
          </div>
          <button onClick={() => openModal('level')} className="self-start sm:self-auto bg-amber-100 text-amber-700 border border-amber-200 px-3 py-1.5 rounded-lg font-semibold text-sm hover:bg-amber-200 flex items-center gap-1"><Plus className="w-4 h-4" /> Add Level</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {levels.length === 0 ? (
            <div className="col-span-full text-sm text-slate-500 py-4 text-center">No levels created yet.</div>
          ) : levels.map((l) => (
            <div key={l.id} className="flex justify-between items-center p-3 border border-slate-200 rounded-lg bg-slate-50 group">
              <div className="min-w-0">
                <p className="font-bold text-slate-900 text-sm truncate">{l.name}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{l.section?.replace('_', ' ')}</p>
              </div>
              <div className={rowActions}>
                <button onClick={() => openModal('level', l)} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Edit level"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => setDeleteTarget({ type: 'level', item: l })} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Delete level"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CLASS CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {classes.length === 0 ? (
          <div className="col-span-full text-center p-8 text-slate-500 bg-white rounded-xl border border-slate-200 shadow-sm">No classes created yet.</div>
        ) : classes.map((c) => {
          const enrolled = c.active_students_count ?? c.students_count ?? 0;
          const cap = c.capacity || 40;
          const classTeacher = c.form_teacher_name || personName(c.form_teacher) || c.class_teacher?.name || c.teacher_name;
          const subjCount = subjectCountByClass.get(Number(c.id))?.size ?? 0;
          return (
            <div key={c.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm group flex flex-col">
              <div className="flex justify-between items-start gap-2 mb-4">
                <div className="min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 truncate">{c.name}{c.arm ? ` · ${c.arm}` : ''}</h3>
                  <p className="text-xs text-slate-500">{c.class_level?.name || c.level_name || 'No level assigned'}</p>
                </div>
                <div className={rowActions}>
                  <button onClick={() => openModal('class', c)} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded" aria-label="Edit class"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setDeleteTarget({ type: 'class', item: c })} className="p-1.5 bg-slate-100 hover:bg-red-200 hover:text-red-600 text-slate-600 rounded" aria-label="Delete class"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-xs font-semibold text-slate-500 uppercase">Capacity</p>
                <p className="text-sm font-medium text-slate-800">{enrolled} of {cap} enrolled</p>
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-green-600 rounded-full" style={{ width: `${Math.min(100, (enrolled / cap) * 100)}%` }} /></div>
              </div>
              <div className="mt-auto space-y-3">
                <div className="flex justify-between items-end gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Class teacher</p>
                    <p className={`text-sm font-bold truncate ${classTeacher ? 'text-slate-900' : 'text-amber-600'}`}>{classTeacher || 'Unassigned'}</p>
                  </div>
                  <button onClick={() => openModal('teacher', c)} className="shrink-0 text-xs font-bold text-[#0f172a] hover:underline flex items-center gap-1 py-1"><UserCheck className="w-3.5 h-3.5" /> {classTeacher ? 'Change' : 'Assign'}</button>
                </div>
                <div className="flex justify-between items-center gap-2 border-t border-slate-100 pt-3">
                  <p className="text-xs text-slate-500"><span className="font-bold text-slate-800">{subjCount}</span> subject{subjCount === 1 ? '' : 's'} with a teacher</p>
                  <button onClick={() => { setFClass(String(c.id)); document.getElementById('assignments')?.scrollIntoView({ behavior: 'smooth' }); }} className="text-xs font-semibold text-green-700 hover:underline">View</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* SUBJECTS */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-900 text-lg">Subjects</h3><p className="text-sm font-semibold text-slate-500">{subjects.length} total</p></div>
          <div className="divide-y divide-slate-100">
            {subjects.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">No subjects added.</p> : subjects.map((s) => {
              const taught = teachersBySubject.get(Number(s.id)) ?? [];
              const names = [...new Set(taught.map((t) => t.teacher))];
              return (
                <div key={s.id} className="flex justify-between items-start gap-3 py-3 group">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 text-sm">{s.name}{s.is_active === false && <span className="ml-2 text-[10px] font-bold uppercase text-slate-400">inactive</span>}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {names.length
                        ? <>{names.join(', ')} · {taught.length} class{taught.length === 1 ? '' : 'es'}</>
                        : <span className="text-amber-600">No teacher assigned</span>}
                    </p>
                  </div>
                  <div className={rowActions}>
                    <button onClick={() => openModal('assignment', { subject_id: s.id })} className="p-1.5 text-slate-400 hover:text-green-700" aria-label="Assign teacher to subject"><UserCheck className="w-4 h-4" /></button>
                    <button onClick={() => openModal('subject', s)} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Edit subject"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteTarget({ type: 'subject', item: s })} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Delete subject"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ASSESSMENT WEIGHTS */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-900 text-lg">Assessment weights</h3>
            <p className={`text-sm font-semibold ${weightTotal === 100 ? 'text-green-700' : 'text-amber-600'}`}>Total {weightTotal}%</p>
          </div>
          {components.length > 0 && weightTotal !== 100 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">Weights should add up to 100% so results compute correctly.</p>
          )}
          <div className="space-y-4">
            {components.length === 0 ? <p className="text-sm text-slate-500 text-center py-4">No weights configured.</p> : components.map((c) => (
              <div key={c.id} className="group">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-bold text-slate-900 flex items-center gap-2">
                    {c.name}
                    <button onClick={() => openModal('weight', c)} className="md:opacity-0 md:group-hover:opacity-100 text-slate-400 hover:text-slate-700 p-1" aria-label="Edit weight"><Edit2 className="w-3.5 h-3.5" /></button>
                  </span>
                  <span className="font-bold text-slate-900">{c.weight}%</span>
                </div>
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-green-700 rounded-full" style={{ width: `${Math.min(100, Number(c.weight) || 0)}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SUBJECT TEACHER ASSIGNMENTS */}
      <div id="assignments" className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 scroll-mt-4">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4">
          <div>
            <h3 className="font-bold text-slate-900 text-lg">Subject Teacher Assignments</h3>
            <p className="text-sm text-slate-500">Assign a teacher to a subject in one or more classes.</p>
          </div>
          <button onClick={() => openModal('assignment')} className="self-start sm:self-auto bg-[#0f172a] text-white px-3 py-2 rounded-lg font-semibold text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Assign Teacher</button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Search teacher, class or subject" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <select value={fClass} onChange={(e) => setFClass(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All classes</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{classLabel(c)}</option>)}
          </select>
          <select value={fTeacher} onChange={(e) => setFTeacher(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All teachers</option>
            {assignedTeacherOptions.map((t) => <option key={t.id} value={t.id}>{personName(t)}</option>)}
          </select>
        </div>
        {(fClass || fTeacher || fSearch) && (
          <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
            <span>{filteredAssignments.length} of {subjectAssignments.length} shown</span>
            <button onClick={() => { setFClass(''); setFTeacher(''); setFSearch(''); }} className="font-semibold text-slate-700 hover:underline">Clear filters</button>
          </div>
        )}

        {assignLoading ? (
          <p className="p-6 text-center text-slate-500 text-sm">Loading assignments…</p>
        ) : filteredAssignments.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg">
            <BookOpen className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            {subjectAssignments.length === 0 ? 'No teacher assignments yet. Use “Assign Teacher” to add one.' : 'No assignments match these filters.'}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr><th className="px-4 py-3">Teacher</th><th className="px-4 py-3">Class</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3 text-right">Action</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAssignments.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <span className="w-8 h-8 rounded-full bg-green-100 text-green-800 text-xs font-bold flex items-center justify-center shrink-0">{initials(teacherOf(a))}</span>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900">{teacherOf(a)}</p>
                            {(a.teacher_phone || a.teacher_email) && <p className="text-xs text-slate-500 truncate">{a.teacher_phone || a.teacher_email}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{classOf(a, classById)}</td>
                      <td className="px-4 py-3 text-slate-700">{subjectOf(a, subjectById)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setDeleteTarget({ type: 'assignment', item: a })} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Remove assignment"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <ul className="md:hidden space-y-2">
              {filteredAssignments.map((a) => (
                <li key={a.id} className="border border-slate-200 rounded-lg p-3 flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-green-100 text-green-800 text-xs font-bold flex items-center justify-center shrink-0">{initials(teacherOf(a))}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm">{teacherOf(a)}</p>
                    <p className="text-xs text-slate-600 mt-0.5">{subjectOf(a, subjectById)} · {classOf(a, classById)}</p>
                    {a.teacher_phone && <p className="text-xs text-slate-400 mt-0.5">{a.teacher_phone}</p>}
                  </div>
                  <button onClick={() => setDeleteTarget({ type: 'assignment', item: a })} className="p-2 -m-1 text-slate-400 hover:text-red-600" aria-label="Remove assignment"><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* ───────────── MODALS ───────────── */}

      {modalType === 'class' && (
        <Modal title={selectedItem ? 'Edit Class' : 'Add New Class'} onClose={closeModal}>
          <form onSubmit={(e) => { e.preventDefault(); classMutation.mutate(Object.fromEntries(new FormData(e.currentTarget))); }} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Class Level</label>
              <select name="class_level_id" defaultValue={selectedItem?.class_level_id || selectedItem?.class_level?.id} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                <option value="">Select a level...</option>
                {levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Class Name</label><input name="name" defaultValue={selectedItem?.name} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. JSS 1" /></div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1">Arm</label><input name="arm" defaultValue={selectedItem?.arm} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. A" /></div>
            </div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Capacity</label><input name="capacity" type="number" min={1} defaultValue={selectedItem?.capacity || 40} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <FormError error={classMutation.error} />
            <ModalButtons onCancel={closeModal} submitLabel="Save Class" isPending={classMutation.isPending} />
          </form>
        </Modal>
      )}

      {modalType === 'teacher' && selectedItem && (
        <Modal title={`Class teacher · ${classLabel(selectedItem)}`} onClose={closeModal}>
          <form onSubmit={(e) => { e.preventDefault(); teacherMutation.mutate(Object.fromEntries(new FormData(e.currentTarget))); }} className="space-y-4">
            <p className="text-sm text-slate-500">The class teacher looks after this class (attendance, remarks). Choosing a new one replaces the current class teacher.</p>
            <StaffSelect name="teacher_id" teachers={teachers} otherStaff={otherStaff} defaultValue={selectedItem.form_teacher_id ?? ''} />
            <FormError error={teacherMutation.error} />
            <ModalButtons onCancel={closeModal} submitLabel="Save class teacher" isPending={teacherMutation.isPending} />
          </form>
        </Modal>
      )}

      {modalType === 'assignment' && (
        <AssignSubjectModal
          teachers={teachers}
          otherStaff={otherStaff}
          classes={classes}
          subjects={subjects.filter((s) => s.is_active !== false)}
          assignments={subjectAssignments}
          initialSubjectId={selectedItem?.subject_id}
          isPending={assignmentMutation.isPending}
          error={assignmentMutation.error}
          onSubmit={(b) => assignmentMutation.mutate(b)}
          onClose={closeModal}
        />
      )}

      {modalType === 'subject' && (
        <Modal title={selectedItem ? 'Edit Subject' : 'Add New Subject'} onClose={closeModal}>
          <form onSubmit={(e) => { e.preventDefault(); subjectMutation.mutate(Object.fromEntries(new FormData(e.currentTarget))); }} className="space-y-4">
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Subject Name</label><input name="name" defaultValue={selectedItem?.name} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. Basic Science" /></div>
            <FormError error={subjectMutation.error} />
            <ModalButtons onCancel={closeModal} submitLabel="Save Subject" isPending={subjectMutation.isPending} />
          </form>
        </Modal>
      )}

      {modalType === 'level' && (
        <Modal title={selectedItem ? 'Edit Level' : 'Add Class Level'} onClose={closeModal}>
          <form onSubmit={(e) => { e.preventDefault(); levelMutation.mutate(Object.fromEntries(new FormData(e.currentTarget))); }} className="space-y-4">
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Level Name</label><input name="name" defaultValue={selectedItem?.name} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. Junior Secondary" /></div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Section</label>
              <select name="section" defaultValue={selectedItem?.section} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                <option value="">Select a section...</option>
                <option value="nursery">Nursery</option>
                <option value="primary">Primary</option>
                <option value="junior_secondary">Junior Secondary</option>
                <option value="senior_secondary">Senior Secondary</option>
              </select>
            </div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Position (1-99)</label><input name="position" type="number" min="1" max="99" defaultValue={selectedItem?.position || 1} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <FormError error={levelMutation.error} />
            <ModalButtons onCancel={closeModal} submitLabel="Save Level" isPending={levelMutation.isPending} />
          </form>
        </Modal>
      )}

      {modalType === 'weight' && selectedItem && (
        <Modal title={`Edit ${selectedItem.name} Weight`} onClose={closeModal}>
          <form onSubmit={(e) => { e.preventDefault(); weightMutation.mutate(Object.fromEntries(new FormData(e.currentTarget))); }} className="space-y-4">
            <div><label className="block text-sm font-semibold text-slate-700 mb-1">Weight (%)</label><input name="weight" type="number" min={0} max={100} defaultValue={selectedItem.weight} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            <p className="text-xs text-slate-500">Other components currently total {weightTotal - Number(selectedItem.weight || 0)}%.</p>
            <FormError error={weightMutation.error} />
            <ModalButtons onCancel={closeModal} submitLabel="Save Weight" isPending={weightMutation.isPending} />
          </form>
        </Modal>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4"><AlertTriangle className="w-6 h-6" /></div>
            <h3 className="font-bold text-slate-900 text-lg mb-2 break-words">{deleteTarget.type === 'assignment' ? 'Remove assignment?' : `Delete ${deleteLabel}?`}</h3>
            <p className="text-sm text-slate-500 mb-4">
              {deleteTarget.type === 'assignment' ? <>{deleteLabel} will be removed. The teacher keeps their account.</> : <>Are you sure you want to delete this {deleteTarget.type}? This action cannot be undone.</>}
            </p>
            {deleteError && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">{deleteError}</p>}
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm hover:bg-slate-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 bg-red-600 rounded-lg text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-60">{deleting ? 'Removing...' : deleteTarget.type === 'assignment' ? 'Remove' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── assign modal ───────────────────────── */

function AssignSubjectModal({ teachers, otherStaff, classes, subjects, assignments, initialSubjectId, isPending, error, onSubmit, onClose }: {
  teachers: any[]; otherStaff: any[]; classes: any[]; subjects: any[]; assignments: any[];
  initialSubjectId?: number; isPending: boolean; error: unknown; onSubmit: (b: any) => void; onClose: () => void;
}) {
  const [teacherId, setTeacherId] = useState('');
  const [subjectId, setSubjectId] = useState(initialSubjectId ? String(initialSubjectId) : '');
  const [picked, setPicked] = useState<Set<number>>(new Set());

  // Who already teaches the chosen subject in each class?
  const takenBy = useMemo(() => {
    const m = new Map<number, string[]>();
    if (!subjectId) return m;
    assignments.filter((a) => String(a.subject_id) === subjectId).forEach((a) => {
      const k = Number(a.school_class_id);
      m.set(k, [...(m.get(k) ?? []), String(a.teacher_id) === teacherId ? 'this teacher' : teacherOf(a)]);
    });
    return m;
  }, [assignments, subjectId, teacherId]);

  const alreadyMine = (cid: number) => (takenBy.get(cid) ?? []).includes('this teacher');
  const toggle = (id: number) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectable = classes.filter((c) => !alreadyMine(Number(c.id)));
  const allPicked = selectable.length > 0 && selectable.every((c) => picked.has(Number(c.id)));

  // Group classes by level for an easier checklist.
  const groups = useMemo(() => {
    const g = new Map<string, any[]>();
    classes.forEach((c) => { const k = c.class_level?.name || 'Other'; g.set(k, [...(g.get(k) ?? []), c]); });
    return [...g.entries()];
  }, [classes]);

  const ids = [...picked].filter((id) => !alreadyMine(id));
  const canSubmit = teacherId && subjectId && ids.length > 0;

  return (
    <Modal title="Assign Subject Teacher" onClose={onClose} wide>
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSubmit) onSubmit({ teacher_id: Number(teacherId), subject_id: Number(subjectId), school_class_ids: ids }); }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Teacher</label>
            <StaffSelect name="teacher_id" teachers={teachers} otherStaff={otherStaff} value={teacherId} onChange={setTeacherId} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Subject</label>
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              <option value="">Select subject...</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-slate-700">Classes <span className="font-normal text-slate-500">({ids.length} selected)</span></label>
            {classes.length > 1 && (
              <button type="button" onClick={() => setPicked(allPicked ? new Set() : new Set(selectable.map((c) => Number(c.id))))} className="text-xs font-semibold text-green-700 hover:underline">
                {allPicked ? 'Clear all' : 'Select all'}
              </button>
            )}
          </div>
          {classes.length === 0 ? (
            <p className="text-sm text-slate-500 border border-dashed rounded-lg p-4 text-center">Create a class first.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {groups.map(([level, cls]) => (
                <div key={level}>
                  <p className="px-3 py-1.5 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500 sticky top-0">{level}</p>
                  {cls.map((c) => {
                    const id = Number(c.id);
                    const mine = alreadyMine(id);
                    const others = (takenBy.get(id) ?? []).filter((n) => n !== 'this teacher');
                    return (
                      <label key={id} className={`flex items-center gap-3 px-3 py-2.5 text-sm ${mine ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}>
                        <input type="checkbox" disabled={mine} checked={mine || picked.has(id)} onChange={() => toggle(id)} className="w-4 h-4 accent-green-600" />
                        <span className="flex-1 min-w-0">
                          <span className="font-medium text-slate-900">{classLabel(c)}</span>
                          {mine && <span className="block text-xs text-slate-500">Already assigned to this teacher</span>}
                          {!mine && others.length > 0 && <span className="block text-xs text-amber-600">Also taught by {others.join(', ')}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <FormError error={error} />
        <ModalButtons onCancel={onClose} submitLabel={ids.length > 1 ? `Assign to ${ids.length} classes` : 'Assign'} isPending={isPending} disabled={!canSubmit} />
      </form>
    </Modal>
  );
}

/* ───────────────────────── small pieces ───────────────────────── */

function StaffSelect({ name, teachers, otherStaff, value, onChange, defaultValue }: {
  name: string; teachers: any[]; otherStaff: any[]; value?: string; onChange?: (v: string) => void; defaultValue?: string | number;
}) {
  const controlled = value !== undefined ? { value, onChange: (e: React.ChangeEvent<HTMLSelectElement>) => onChange?.(e.target.value) } : { defaultValue };
  return (
    <>
      <select name={name} required {...controlled} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
        <option value="">Select teacher...</option>
        {teachers.length > 0 && (
          <optgroup label="Teachers">{teachers.map((t) => <option key={t.id} value={t.id}>{personName(t)}{t.profile?.designation ? ` — ${t.profile.designation}` : ''}</option>)}</optgroup>
        )}
        {otherStaff.length > 0 && (
          <optgroup label="Other staff">{otherStaff.map((t) => <option key={t.id} value={t.id}>{personName(t)} ({t.role})</option>)}</optgroup>
        )}
      </select>
      {teachers.length === 0 && otherStaff.length === 0 && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> No active staff yet. Add teachers under People &amp; Access → Staff.</p>
      )}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'green' | 'amber' }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 leading-tight">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 ${tone === 'amber' && value > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

function FormError({ error }: { error: unknown }) {
  const msg = errorText(error);
  return msg ? <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{msg}</p> : null;
}

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className={`bg-white w-full ${wide ? 'sm:max-w-lg' : 'sm:max-w-md'} rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center gap-3 bg-slate-50 shrink-0">
          <h3 className="font-bold text-slate-900 truncate">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 sm:p-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ModalButtons({ onCancel, submitLabel, isPending, disabled }: { onCancel: () => void; submitLabel: string; isPending: boolean; disabled?: boolean }) {
  return (
    <div className="pt-2 flex gap-3">
      <button type="button" onClick={onCancel} className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm hover:bg-slate-50">Cancel</button>
      <button type="submit" disabled={isPending || disabled} className="flex-1 py-2.5 bg-green-600 rounded-lg text-white font-semibold text-sm hover:bg-green-700 disabled:opacity-50">{isPending ? 'Saving...' : submitLabel}</button>
    </div>
  );
}
